import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { getSupabase, isSupabaseConfigured } from './supabase'

type AuthState = {
  user: User | null
  session: Session | null
  loading: boolean
  configured: boolean
}

type AuthContextValue = AuthState & {
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: isSupabaseConfigured,
    configured: isSupabaseConfigured,
  })

  // Lưu user.id của session trước để phát hiện đổi tài khoản (logout-login user
  // khác trong cùng tab). Khi user.id đổi → cache TanStack Query của user trước
  // phải bị xoá để tránh leak: vd menu hiện theo quyền cũ, list khách hàng vẫn
  // còn dữ liệu user trước.
  const lastUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isSupabaseConfigured) return

    const supabase = getSupabase()
    let cancelled = false

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      lastUserIdRef.current = data.session?.user?.id ?? null
      setState((prev) => ({
        ...prev,
        session: data.session,
        user: data.session?.user ?? null,
        loading: false,
      }))
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null
      const prevUserId = lastUserIdRef.current

      // Clear toàn bộ cache khi:
      //   - SIGNED_OUT: user vừa logout → không được giữ data cá nhân
      //   - SIGNED_IN với user khác: switch account → tránh hiển thị data cũ
      // KHÔNG clear khi TOKEN_REFRESHED hoặc USER_UPDATED — đó vẫn là user cũ.
      const isLogout = event === 'SIGNED_OUT' || nextUserId === null
      const isUserSwitch =
        nextUserId !== null && prevUserId !== null && nextUserId !== prevUserId

      if (isLogout || isUserSwitch) {
        queryClient.clear()
      }

      lastUserIdRef.current = nextUserId
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }))
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      async signInWithPassword(email, password) {
        const supabase = getSupabase()
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      },
      async signOut() {
        if (!isSupabaseConfigured) return
        const supabase = getSupabase()
        await supabase.auth.signOut()
      },
    }),
    [state],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
